import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import remarkCodeTitles from 'remark-code-titles';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';

function MarkdownRenderer({ children }) {
    try {
        return (
            <Markdown
                remarkPlugins={[remarkGfm, remarkCodeTitles]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    code({
                        node,
                        inline,
                        className = '',
                        children: codeChildren,
                        ...props
                    }) {
                        const match = /language-(\w+)/.exec(className);
                        const language = match?.[1] || '';
                        const codeTitle = node?.data?.hProperties?.title;

                        return !inline && language ? (
                            <div style={{ marginBottom: '1rem' }}>
                                {codeTitle && (
                                    <div
                                        style={{
                                            backgroundColor: '#2d2d2d',
                                            color: '#fff',
                                            padding: '0.25rem 0.75rem',
                                            fontSize: '0.75rem',
                                            borderTopLeftRadius: '5px',
                                            borderTopRightRadius: '5px',
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        {codeTitle}
                                    </div>
                                )}
                                <SyntaxHighlighter
                                    style={dracula}
                                    PreTag="div"
                                    language={language}
                                    customStyle={{
                                        margin: 0,
                                        borderTopLeftRadius: codeTitle
                                            ? 0
                                            : '5px',
                                        borderTopRightRadius: codeTitle
                                            ? 0
                                            : '5px',
                                        borderBottomLeftRadius: '5px',
                                        borderBottomRightRadius: '5px'
                                    }}
                                    {...props}
                                >
                                    {String(codeChildren).replace(/\n$/, '')}
                                </SyntaxHighlighter>
                            </div>
                        ) : (
                            <code className={className} {...props}>
                                {codeChildren}
                            </code>
                        );
                    },

                    p({ node, children, ...props }) {
                        return (
                            <p
                                style={{
                                    marginBottom: '0.5rem',
                                    lineHeight: '1.5',
                                    whiteSpace: 'pre-line'
                                }}
                                {...props}
                            >
                                {children}
                            </p>
                        );
                    },

                    table({ children, ...props }) {
                        return (
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    marginBottom: '1rem'
                                }}
                                {...props}
                            >
                                {children}
                            </table>
                        );
                    },

                    th({ children, ...props }) {
                        return (
                            <th
                                style={{
                                    textAlign: 'left',
                                    fontWeight: 'bold',
                                    borderBottom: '2px solid #555',
                                    padding: '0.75rem 1rem' // Adjusted padding for consistency
                                }}
                                {...props}
                            >
                                {children}
                            </th>
                        );
                    },

                    tbody({ children, ...props }) {
                        return <tbody {...props}>{children}</tbody>;
                    },

                    td({ children, ...props }) {
                        return (
                            <td
                                style={{
                                    padding: '0.75rem 1rem', // Adjusted padding to match th
                                    borderBottom: '1px solid #555',
                                    verticalAlign: 'top' // Ensures proper alignment
                                }}
                                {...props}
                            >
                                {children}
                            </td>
                        );
                    }
                }}
            >
                {children}
            </Markdown>
        );
    } catch (error) {
        console.error('Markdown rendering error:', error);

        // Fallback: basic <pre> with raw children content
        return (
            <pre
                style={{
                    backgroundColor: '#f5f5f5',
                    color: '#555',
                    padding: '1rem',
                    borderRadius: '8px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                }}
            >
                {typeof children === 'string'
                    ? children
                    : 'Error rendering content'}
            </pre>
        );
    }
}

export default MarkdownRenderer;
